<template>
  <b-modal v-model="model" @update:model-value="emit('input', $event)" size="2xl" data-e2e="experience-modal">
    <template #header>
      <div class="px-10 pt-10 pb-2">
       <div class="mb-5">
          <h1 class="mb-2">
            <span v-if="action === 'create'">{{ t('modal_create_experience.create_experience') }}</span>
            <span v-else>{{ t('modal_create_experience.edit_experience') }} {{ createdExperience?.title || props.experience?.title }}</span>
          </h1>
          <div class="flex items-center gap-3">
            <BButton v-if="currentStep !== 0 && false" size="xs" light @click="currentStep = Math.max(0, currentStep - 1)" prependIcon="solar:arrow-left-bold">
              Retour
            </BButton>
            <p v-if="getDescriptionText" class="text-white font-semibold text-lg">{{ getDescriptionText }}</p>
          </div>
       </div>
        <b-stepper
          v-if="[0,1,2,3].includes(currentStep)"
          :steps="totalSteps"
          :currentStep="currentStep"
          v-model:currentStep="stepperCurrentStep"
          :canGoNext="!submitDisabled"
          :optionalSteps="[3]"
          :optionalLabel="t('modal_create_experience.optional_badge')"
          data-e2e="experience-stepper"
        />
      </div>
    </template>
    <template #default>
      <div class="px-10 py-5">
        <b-modal-create-experience-step1
          v-show="currentStep === 0"
          @change="(event) => step1Data = event"
          @logo-request="onLogoRequest"
          :experience="createdExperience"
          :initial-data="draftStep1Data"
          :logo-request-pending="logoRequestPending"
        />
        <b-modal-create-experience-step2
          v-show="currentStep === 1"
          @change="(event) => step2Data = event"
          :experience="createdExperience"
          :initial-data="draftStep2Data"
        />
        <b-modal-create-experience-step3
          v-show="currentStep === 2"
          @change="(event) => step3Data = event"
          :experience="createdExperience"
          :initial-data="draftStep3Data"
        />
        <b-modal-create-experience-step4
          v-show="currentStep === 3"
          @change="(event) => step4Data = event"
          :experience="createdExperience"
          :current-user-email="user.email"
          :initial-data="draftStep4Data"
        />
      </div>
    </template>
    <template #footer>
      <div class="px-10 pb-10 pt-2 flex gap-4">
        <b-button v-if="false" light block @click="emit('update:modelValue', false);">
          {{ t('modal_create_mission.later') }}
        </b-button>
        <b-button
          v-if="currentStep === 3"
          light
          block
          @click="skipFeedbackAndClose"
          data-e2e="experience-skip-button"
        >
          {{ t('modal_feedback.skip_button') }}
        </b-button>
        <b-button
          v-if="currentStep === 2"
          light
          block
          @click="onAddReferences"
          :disabled="submitDisabled"
          :loading="loading"
          data-e2e="experience-add-references-button"
        >
          {{ t('modal_create_experience.add_references_optional') }}
        </b-button>
        <b-button
          @click="onSubmitMission"
          :disabled="submitDisabled"
          :loading="loading"
          block
          data-e2e="experience-submit-button"
        >
          {{ getSubmitButtonText() }}
        </b-button>
      </div>
    </template>
  </b-modal>
</template>

<script lang="ts" setup>
import BModal from '~/components/modal/BModal.vue';
import BStepper from '~/components/stepper/BStepper.vue';
import BButton from '~/components/forms/BButton.vue';
import BModalCreateExperienceStep1 from "~/components/modal/experience/BModalCreateExperienceStep1.vue";
import BModalCreateExperienceStep2 from "~/components/modal/experience/BModalCreateExperienceStep2.vue";
import BModalCreateExperienceStep3 from "~/components/modal/experience/BModalCreateExperienceStep3.vue";
import BModalCreateExperienceStep4 from "~/components/modal/experience/BModalCreateExperienceStep4.vue";
import type { ComboboxItem } from "~/components/forms/BCombobox.vue";
import type { Experience } from '~/api/v2/experiences/ExperienceRepository';
import { useFeedbackStore } from '~/stores/v2/feedback/useFeedbackStore';
import { authStore } from '~/stores/auth';
import { storeToRefs } from 'pinia';
import { useTracking } from '~/composables/useTracking';

interface Props {
  experience?: Experience
}

const props = defineProps<Props>()

const { t } = useI18n();
const { trackEvent } = useTracking();
const model = defineModel({ default: false });
const experienceStore = useExperienceStore();
const route = useRoute();
const auth = authStore();
const { user } = storeToRefs(auth);

// Reactive state
const currentStep = ref(0);
const stepperCurrentStep = computed({
  get() {
    return currentStep.value;
  },
  set(newValue: number) {
    if (newValue < currentStep.value) {
      currentStep.value = newValue;
    } else if (newValue > currentStep.value) {
      if (currentStep.value === 2) {
        onAddReferences();
        return;
      }
      onSubmitMission().then(() => {
        if (!submitDisabled.value) {
          currentStep.value = newValue;
        }
      });
    }
  }
});
const totalSteps = ref(4);
const step1Data = ref({});
const step2Data = ref({});
const step3Data = ref({});
const step4Data = ref({});
const loading = ref(false);
const createdExperience = ref<Partial<Experience> | null>(null);
const logoRequestPending = ref(false);
const action = ref('create');
const isPublic = ref(true);
const selectedUser = ref<ComboboxItem | null>(null);
const isLoadingDetails = ref(false);

// Emits
const emit = defineEmits(['update:modelValue', 'experience-created']);

// Computed
const submitDisabled = computed(() => {
  switch (currentStep.value) {
    case 0: {
      const base = !!(
        step1Data.value.company_identifier &&
        step1Data.value.industry_id &&
        step1Data.value.company_size_id &&
        step1Data.value.start_at &&
        step1Data.value.contract_type
      );
      const endDateValid = step1Data.value.is_current_position || !!step1Data.value.end_at;
      // Dates are "YYYY-MM-DD" strings → string comparison is chronological.
      // Block submission when the end date is before the start date.
      const endBeforeStart = !!(
        step1Data.value.start_at &&
        step1Data.value.end_at &&
        step1Data.value.end_at < step1Data.value.start_at
      );
      return !(base && endDateValid) || endBeforeStart;
    }
    case 1:
      return !(
        step2Data.value.deal_size_ids?.length > 0 &&
        step2Data.value.persona_ids?.length > 0 &&
        step2Data.value.market_segment_ids?.length > 0
      );
    case 2:
      return !(
        step3Data.value.name &&
        step3Data.value.skill_ids?.length > 0
      );
    case 3:
      return !step4Data.value?.isValid;
  }
});

const experienceTemp = computed(() => {
  const base: Record<string, any> = {
    budget: createdExperience.value?.budget,
    mobility_type: createdExperience.value?.mobility_type,
    company_size_id: createdExperience.value?.company_size?.company_size_id,
  };
  const merged = { ...base };
  for (const stepData of [step1Data.value, step2Data.value, step3Data.value]) {
    for (const [key, value] of Object.entries(stepData)) {
      if (value !== undefined) merged[key] = value;
    }
  }
  return { ...merged, is_public: true };
});

const getDescriptionText = computed(() => {
  switch (currentStep.value) {
    case 0:
      return t('modal_create_experience.step1_description');
    case 1:
      return t('modal_create_experience.step2_description');
    case 2:
      return t('modal_create_experience.step3_description');
    case 3:
      return t('modal_create_experience.step4_description');
    default:
      return null;
  }
})


// Methods
const getSubmitButtonText = () => {
  if (currentStep.value < 2) {
    return t('modal_create_mission.next_step');
  } else if (currentStep.value === 2) {
    return t('modal_create_experience.finish_button');
  } else {
    return t('modal_feedback.send_button');
  }
};

const resetModal = (clearDraft = false) => {
  currentStep.value = 0;
  step1Data.value = {};
  step2Data.value = {};
  step3Data.value = {};
  step4Data.value = {};
  createdExperience.value = null;
  logoRequestPending.value = false;
  action.value = 'create';
  isPublic.value = false;
  selectedUser.value = null;

  if (clearDraft) {
    experienceStore.clearDraft();
    draftStep1Data.value = {};
    draftStep2Data.value = {};
    draftStep3Data.value = {};
    draftStep4Data.value = {};
  }
};

// Track if we're currently restoring a draft to prevent circular updates
const isRestoringDraft = ref(false);

// Simple refs for initial data - only set once during draft restoration
const draftStep1Data = ref({});
const draftStep2Data = ref({});
const draftStep3Data = ref({});
const draftStep4Data = ref({});

const saveDraftToStore = () => {
  if (action.value !== 'create') return;

  experienceStore.saveDraft({
    currentStep: currentStep.value,
    step1Data: { ...step1Data.value },
    step2Data: { ...step2Data.value },
    step3Data: { ...step3Data.value },
    step4Data: { ...step4Data.value },
  });
};

const restoreDraftFromStore = () => {
  const draft = experienceStore.getDraft();
  if (draft && action.value === 'create') {
    isRestoringDraft.value = true;
    currentStep.value = draft.currentStep;
    step1Data.value = { ...draft.step1Data };
    step2Data.value = { ...draft.step2Data };
    step3Data.value = { ...draft.step3Data };
    step4Data.value = { ...draft.step4Data };
    draftStep1Data.value = { ...draft.step1Data };
    draftStep2Data.value = { ...draft.step2Data };
    draftStep3Data.value = { ...draft.step3Data };
    draftStep4Data.value = { ...draft.step4Data };
    nextTick(() => {
      isRestoringDraft.value = false;
    });
  } else {
    draftStep1Data.value = {};
    draftStep2Data.value = {};
    draftStep3Data.value = {};
    draftStep4Data.value = {};
  }
};

const fetchExperienceDetails = async (experienceId: string) => {
  if (!experienceId) return;

  isLoadingDetails.value = true;
  experienceStore.fetchExperience(experienceId)
      .then((response) => {
        createdExperience.value = response
      })
      .finally(() => {
        isLoadingDetails.value = false;
      });

};

// Step processing strategies
const processStep = async (step: number): Promise<any> => {
  switch (step) {
    case 0:
    case 1:
      return { success: true };
    case 2:
      return { success: true };
    case 3:
      return { success: true };

    default:
      throw new Error(`Invalid step: ${step}`);
  }
};

const finalizeSubmission = async () => {
  const feedbackStore = useFeedbackStore();

  const validClientEmails = step4Data.value.clientEmails.filter(email => email.trim() !== '');
  const validManagerEmails = step4Data.value.managerEmails.filter(email => email.trim() !== '');

  if (validClientEmails.length > 0 || validManagerEmails.length > 0) {
    await Promise.all(
        validClientEmails.map(email => feedbackStore.createFeedback({
          email,
          feedback_type: 'client',
          experience_id: createdExperience.value.id
        }))
            .concat(validManagerEmails.map(email => feedbackStore.createFeedback({
              email,
              feedback_type: 'manager',
              experience_id: createdExperience.value.id
            })))
    );
  }

  emit('update:modelValue', false);
  emit('experience-created');

  return { success: true };
};

// Logo corrections are only proposable in edit mode (an experience already
// exists) and go through the admin-moderated logo-request flow, never a
// direct upload at create time.
const onLogoRequest = async (file: File) => {
  const experienceId = createdExperience.value?.id;
  if (!experienceId) return;
  logoRequestPending.value = true;
  try {
    await experienceStore.submitExperienceLogoRequest(experienceId, file);
  } catch (error) {
    // Allow the user to retry: only the request submission failed.
    console.error('Logo request submission failed:', error);
    logoRequestPending.value = false;
  }
};

const persistExperience = async () => {
  if (action.value === 'update' && createdExperience.value) {
    await experienceStore.updateExperience(createdExperience.value.id, {
      ...experienceTemp.value,
      is_public: isPublic.value
    });
    await experienceStore.fetchExperiences();
  } else {
    const response = await experienceStore.createExperience(experienceTemp.value);
    createdExperience.value = response;
    experienceStore.clearDraft();
    trackEvent("experience", "experience_created");
  }
};

const createExperienceAndAdvance = async () => {
  await persistExperience();
  currentStep.value++;
};

const createExperienceAndClose = async () => {
  await persistExperience();
  emit('update:modelValue', false);
  emit('experience-created');
};

const skipFeedbackAndClose = () => {
  emit('update:modelValue', false);
  emit('experience-created');
};

const onAddReferences = async () => {
  if (loading.value) return;
  loading.value = true;
  try {
    await createExperienceAndAdvance();
  } catch (error) {
    console.error('Error submitting mission:', error);
  } finally {
    loading.value = false;
  }
};

const onSubmitMission = async () => {
  if (loading.value) return;

  loading.value = true;

  try {
    if (currentStep.value < 2) {
      currentStep.value++;
    } else if (currentStep.value === 2) {
      await createExperienceAndClose();
    } else if (currentStep.value === 3) {
      await finalizeSubmission();
    }
  } catch (error) {
    console.error('Error submitting mission:', error);
  } finally {
    loading.value = false;
  }
};

const router = useRouter();

// Watchers
watch(() => model.value, (newValue) => {
  if (newValue) {
    if (action.value === 'create' && !props.experience) {
      restoreDraftFromStore();
    }
  } else {
    if (action.value === 'create' && !createdExperience.value) {
      saveDraftToStore();
    }

    if (route.query.experience || route.query.action) {
      const query = { ...route.query };
      delete query.experience;
      delete query.action;
      delete query.step;
      router.replace({ path: route.path, query });
    }

    resetModal();
  }
});

watch(() => route.query, (newValue) => {
  if (newValue.experience && newValue.action === 'update') {
    action.value = newValue.action;
    model.value = true;
    fetchExperienceDetails(newValue.experience as string);
    if (newValue.step) {
      currentStep.value = parseInt(newValue.step as string, 10);
    }
  }
}, { immediate: true });
</script>
